'use client';

import { motion } from 'motion/react';
import { X, View, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, Suspense, useRef } from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';

import type { Swiper as SwiperType } from 'swiper';

const ContactFormModal = dynamic(() => import('./ContactFormModal').then(mod => ({ default: mod.ContactFormModal })), { ssr: false });

const galleryCategories = [
  {
    id: 1,
    name: 'Apartments',
    images: [
      { url: 'images/gallery/apartments-ld/apartment-1.jpg', title: 'Living Room' },
      { url: 'images/gallery/apartments-ld/apartment-2.jpeg', title: 'Bedroom' },
      { url: 'images/gallery/apartments-ld/apartment-3.jpeg', title: 'Kitchen' },
      { url: 'images/gallery/apartments-ld/apartment-4.jpeg', title: 'Bathroom' },
      { url: 'images/gallery/apartments-ld/apartment-5.jpeg', title: 'Dining Area' },
      { url: 'images/gallery/apartments-ld/apartment-6.jpeg', title: 'Balcony View' },
    ],
  },
  {
    id: 2,
    name: 'Indoor',
    images: [
      { url: 'images/gallery/indoor-ld/indoor-1.jpeg', title: 'Exterior Day' },
      { url: 'images/gallery/indoor-ld/indoor-2.jpeg', title: 'Exterior Night' },
      { url: 'images/gallery/indoor-ld/indoor-3.jpeg', title: 'Lobby' },
      { url: 'images/gallery/indoor-ld/indoor-4.jpeg', title: 'Corridor' },
      { url: 'images/gallery/indoor-ld/indoor-5.jpeg', title: 'Gym' },
    ],
  },
  {
    id: 3,
    name: 'Outdoor',
    images: [
      { url: 'images/gallery/outdoor-ld/oudoor-1.jpeg', title: 'Pool' },
      { url: 'images/gallery/outdoor-ld/outdoor-2.jpeg', title: 'Gym' },
      { url: 'images/gallery/outdoor-ld/outdoor-3.jpeg', title: 'Lounge' },
      { url: 'images/gallery/outdoor-ld/outdoor-4.jpeg', title: 'Garden' },
    ],
  },
  {
    id: 4,
    name: 'Community',
    images: [
      { url: 'images/gallery/community-ld/community-1.jpeg', title: 'Neighborhood' },
      { url: 'images/gallery/community-ld/community-2.jpeg', title: 'Surroundings' },
      { url: 'images/gallery/community-ld/community-3.jpeg', title: 'Surroundings' },
      { url: 'images/gallery/community-ld/community-4.jpeg', title: 'Surroundings' },
      { url: 'images/gallery/community-ld/community-5.jpeg', title: 'Surroundings' },
    ],
  },
];

export function GallerySection() {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<typeof galleryCategories[0] | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const swiperRefs = useRef<Record<number, SwiperType | null>>({});
  const autoplayDelays = [5000, 5800, 6600, 7400];

  const openLightbox = (category: typeof galleryCategories[0], imageIndex: number) => {
    setCurrentCategory(category);
    setCurrentImageIndex(imageIndex);
    setIsLightboxOpen(true);
  };

  return (
    <>
      <section id="gallery" className="bg-white py-24 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          {/* CTA Section - Mobile Only - Before Gallery Title */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative h-80 overflow-hidden md:hidden bg-[#c19964] mb-24"
          >
            <div className="flex flex-col items-start justify-center p-8 h-full">
              <h3 className="text-white text-lg tracking-[0.15em] font-light mb-4 text-left">
                Interested?
              </h3>
              <p className="text-white/70 text-base tracking-wide mb-6 text-left">
                Let us know if you have any questions about Gate Eleven Residences.
              </p>
              <button
                onClick={() => setIsFormModalOpen(true)}
                className="bg-white text-black px-8 py-3 tracking-[0.15em] text-sm font-light hover:bg-white/90 transition-colors duration-300"
              >
                Book now
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8 md:mb-20 lg:mb-20"
          >
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="h-[1px] bg-black w-[80px] mx-auto mb-8 origin-center"
            />
            <h2 className="text-black tracking-[0.15em] font-light text-xl md:text-4xl">GALLERY</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryCategories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative h-80 overflow-hidden"
              >
                <Swiper
                  modules={[Pagination, Autoplay]}
                  pagination={{ clickable: true }}
                  autoplay={{ delay: autoplayDelays[index], disableOnInteraction: false, pauseOnMouseEnter: true }}
                  spaceBetween={0}
                  slidesPerView={1}
                  className="h-full gallery-category-swiper"
                  onSwiper={(swiper) => { swiperRefs.current[category.id] = swiper; }}
                  onClick={(swiper, event) => {
                    const target = event.target as HTMLElement;
                    if (!target.closest('.swiper-pagination') && !target.closest('.gallery-nav-btn')) {
                      openLightbox(category, swiper.activeIndex);
                    }
                  }}
                >
                  {category.images.map((image, imgIndex) => (
                    <SwiperSlide key={imgIndex}>
                      <div className="relative h-full cursor-pointer group">
                        <Image
                          src={`/${image.url}`}
                          alt={image.title}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-300"
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          loading="lazy"
                          quality={85}
                          onError={() => console.log('Image failed to load:', image.url)}
                          onLoad={() => console.log('Image loaded successfully:', image.url)}
                        />
                        <div className="absolute inset-0 flex flex-col items-center justify-end pb-6" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 30%)' }}>
                          <h3 className="text-white text-xl tracking-[0.15em] font-light mb-2">{category.name}</h3>
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                {/* Bottom Navigation Arrows */}
                <div className="absolute bottom-3 right-3 flex gap-2 z-10">
                  <button
                    className="gallery-nav-btn w-8 h-8 rounded-full flex items-center justify-center bg-[#c19964]/70 hover:bg-[#c19964] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      const swiper = swiperRefs.current[category.id];
                      swiper?.slidePrev();
                      swiper?.autoplay.stop();
                      swiper?.autoplay.start();
                    }}
                    aria-label="Previous"
                  >
                    <ChevronLeft className="w-4 h-4 text-white" />
                  </button>
                  <button
                    className="gallery-nav-btn w-8 h-8 rounded-full flex items-center justify-center bg-[#c19964]/70 hover:bg-[#c19964] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      const swiper = swiperRefs.current[category.id];
                      swiper?.slideNext();
                      swiper?.autoplay.stop();
                      swiper?.autoplay.start();
                    }}
                    aria-label="Next"
                  >
                    <ChevronRight className="w-4 h-4 text-white" />
                  </button>
                </div>
              </motion.div>
            ))}

            {/* 360° Virtual Tour Tile - removed from here, moved to bottom */}

            {/* YouTube Video - Mobile Only */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="relative h-80 overflow-hidden md:hidden bg-black"
            >
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube-nocookie.com/embed/308yw8Eu6VA"
                title="Gate Eleven Residences By Amwaj Development"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </motion.div>

            {/* YouTube Video + CTA Section - Desktop (Spans 2 Columns) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="hidden md:block relative h-80 overflow-hidden md:col-span-2 lg:col-span-2 bg-black"
            >
              <div className="flex h-full">
                {/* Video - 16:9 Aspect Ratio */}
                <div className="w-auto h-full aspect-video flex-shrink-0">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube-nocookie.com/embed/308yw8Eu6VA"
                    title="Gate Eleven Residences By Amwaj Development"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>

                {/* CTA Section */}
                <div className="flex-1 bg-[#c19964] flex flex-col items-start justify-center p-8">
                  <h3 className="text-white text-lg tracking-[0.15em] font-light mb-4 text-left">
                    Interested?
                  </h3>
                  <p className="text-white/70 text-sm tracking-wide mb-6 text-left max-w-md">
                    Let us know if you have any questions about Gate Eleven Residences.                  </p>
                  <button
                    onClick={() => setIsFormModalOpen(true)}
                    className="bg-white text-black px-8 py-3 tracking-[0.15em] text-sm font-light hover:bg-white/90 transition-colors duration-300"
                  >
                    Book now
                  </button>
                </div>
              </div>
            </motion.div>

            {/* 360° Virtual Tour Tile - removed */}
          </div>
        </div>
      </section>

      {/* 360° Virtual Tour - Full Width Section */}
      <a
        href="https://momento360.com/e/uc/240c6f132e5f4d60ba5c31e59e232da0?utm_campaign=embed&utm_source=interiors&size=medium&display-plan=true"
        target="_blank"
        rel="noopener noreferrer"
        className="relative w-full block"
      >
        <Image
          src="/images/gallery/panoramic-desktop-gate.jpg"
          alt="360° Virtual Tour - Gate Eleven Residences"
          width={0}
          height={0}
          sizes="100vw"
          className="w-full h-auto hidden md:block"
          quality={90}
        />
        <Image
          src="/images/gallery/panoramic-mobile-gate.jpg"
          alt="360° Virtual Tour - Gate Eleven Residences"
          width={0}
          height={0}
          sizes="100vw"
          className="w-full h-auto block md:hidden"
          quality={90}
        />
      </a>

      {/* Lightbox Modal */}
      {isLightboxOpen && currentCategory && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
          onClick={() => setIsLightboxOpen(false)}
        >
          <button
            onClick={() => setIsLightboxOpen(false)}
            className="absolute top-6 right-6 z-[110] text-white hover:text-white/70 transition-colors"
          >
            <X className="w-8 h-8" />
          </button>

          <div className="w-full h-full flex items-center justify-center px-4" onClick={(e) => e.stopPropagation()}>
            <div className="w-full max-w-6xl">
              <h3 className="text-white text-2xl tracking-[0.15em] font-light text-center mb-6">{currentCategory.name}</h3>
              <Swiper
                modules={[Navigation, Pagination, Zoom]}
                navigation
                pagination={{ clickable: true }}
                zoom
                initialSlide={currentImageIndex}
                className="w-full h-[70vh]"
                spaceBetween={50}
              >
                {currentCategory.images.map((image, index) => (
                  <SwiperSlide key={index} className="flex items-center justify-center">
                    <div className="swiper-zoom-container">
                      <Image
                        src={`/${image.url}`}
                        alt={image.title}
                        width={1200}
                        height={800}
                        className="w-full h-full object-contain"
                        sizes="100vw"
                        loading="lazy"
                        quality={85}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </motion.div>
      )}

      {/* Contact Form Modal */}
      <Suspense fallback={<div />}>
        <ContactFormModal
          isOpen={isFormModalOpen}
          onClose={() => setIsFormModalOpen(false)}
        />
      </Suspense>
    </>
  );
}